import React, { useState } from 'react';
import {
  XMarkIcon,
  PencilSquareIcon,
  WrenchScrewdriverIcon,
  BanknotesIcon,
  DocumentTextIcon,
  PhotoIcon,
  TruckIcon
} from '@heroicons/react/24/outline';
import { Vehicle } from '../../types/vehicle';
import { useVehicleServices } from '../../hooks/useVehicleServices';
import { useVehicleExpenses } from '../../hooks/useVehicleExpenses';
import { useLanguage } from '../../contexts/LanguageContext';
import { formatDate } from '../../utils/dateHelpers';
import MileageRecorder from './MileageRecorder';
import MileageHistory from './MileageHistory';
import VehiclePhotoUploader from './VehiclePhotoUploader';
import VehicleDocumentUploader from './VehicleDocumentUploader';
import ServiceRecorder from './ServiceRecorder';
import ExpenseRecorder from './ExpenseRecorder';

interface VehicleDetailsProps {
  vehicle: Vehicle;
  onClose: () => void;
  onEdit: () => void;
}

const VehicleDetails: React.FC<VehicleDetailsProps> = ({ vehicle, onClose, onEdit }) => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState<'info' | 'services' | 'expenses' | 'documents'>('info');
  const { services, loading: servicesLoading } = useVehicleServices(vehicle.id, 10);
  const { expenses, loading: expensesLoading } = useVehicleExpenses(vehicle.id, 10);

  const getStatusBadge = (status: string) => {
    const badges = {
      active: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
      inactive: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
      maintenance: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
      sold: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
    };

    return (
      <span className={`px-3 py-1 text-sm font-medium rounded-full ${badges[status as keyof typeof badges]}`}>
        {t(`fleet.status.${status}`)}
      </span>
    );
  };

  const getFuelTypeLabel = (fuelType: string) => {
    return t(`fleet.form.fuelTypes.${fuelType}`) || fuelType;
  };

  const getServiceTypeLabel = (type: string) => {
    const labels = {
      maintenance: 'Karbantartás',
      repair: 'Javítás',
      inspection: 'Ellenőrzés',
      tire_change: 'Gumiabroncs csere',
      oil_change: 'Olajcsere',
      other: 'Egyéb'
    };
    return labels[type as keyof typeof labels] || type;
  };

  const getExpenseCategoryLabel = (category: string) => {
    const labels = {
      fuel: 'Üzemanyag',
      insurance: 'Biztosítás',
      parking: 'Parkolás',
      toll: 'Útdíj',
      fine: 'Bírság',
      service: 'Szerviz',
      other: 'Egyéb'
    };
    return labels[category as keyof typeof labels] || category;
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop */}
        <div className="fixed inset-0 transition-opacity bg-gray-900 bg-opacity-75 backdrop-blur-sm" onClick={onClose} />

        {/* Modal */}
        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full">
          {/* Header with gradient */}
          <div className="relative bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-700 dark:to-indigo-700 px-6 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3">
                  <TruckIcon className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white">
                    {vehicle.licensePlate}
                  </h3>
                  <p className="text-sm text-blue-100">
                    {vehicle.make} {vehicle.model} ({vehicle.year})
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {getStatusBadge(vehicle.status)}
                <button
                  onClick={onEdit}
                  className="inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-lg text-sm font-medium text-white hover:bg-white/30 transition-all"
                >
                  <PencilSquareIcon className="w-4 h-4 mr-2" />
                  Szerkesztés
                </button>
                <button
                  onClick={onClose}
                  className="text-white/80 hover:text-white transition-colors"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>
            </div>
          </div>

          {/* Tabs - Modern design */}
          <div className="bg-gray-50 dark:bg-gray-900/50 px-6 pt-4">
            <nav className="flex gap-2">
              <button
                onClick={() => setActiveTab('info')}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium rounded-t-lg transition-all ${
                  activeTab === 'info'
                    ? 'bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-white/50 dark:hover:bg-gray-800/50'
                }`}
              >
                <DocumentTextIcon className="w-5 h-5" />
                Információk
              </button>
              <button
                onClick={() => setActiveTab('services')}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium rounded-t-lg transition-all ${
                  activeTab === 'services'
                    ? 'bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-white/50 dark:hover:bg-gray-800/50'
                }`}
              >
                <WrenchScrewdriverIcon className="w-5 h-5" />
                Szervizek
                {services.length > 0 && (
                  <span className="ml-1 px-2 py-0.5 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full">
                    {services.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveTab('expenses')}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium rounded-t-lg transition-all ${
                  activeTab === 'expenses'
                    ? 'bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-white/50 dark:hover:bg-gray-800/50'
                }`}
              >
                <BanknotesIcon className="w-5 h-5" />
                Költségek
                {expenses.length > 0 && (
                  <span className="ml-1 px-2 py-0.5 text-xs bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-full">
                    {expenses.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveTab('documents')}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium rounded-t-lg transition-all ${
                  activeTab === 'documents'
                    ? 'bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-white/50 dark:hover:bg-gray-800/50'
                }`}
              >
                <PhotoIcon className="w-5 h-5" />
                Dokumentumok
              </button>
            </nav>
          </div>

          {/* Content */}
          <div className="px-6 py-6 max-h-[calc(100vh-300px)] overflow-y-auto">
            {/* Info Tab */}
            {activeTab === 'info' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Alapadatok */}
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Alapadatok</h4>
                  
                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400">Rendszám</label>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{vehicle.licensePlate}</p>
                  </div>

                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400">Márka / Modell</label>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {vehicle.make} {vehicle.model}
                    </p>
                  </div>

                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400">Gyártási év</label>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{vehicle.year}</p>
                  </div>

                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400">Üzemanyag</label>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {getFuelTypeLabel(vehicle.fuelType)}
                    </p>
                  </div>

                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400">Km óra</label>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {vehicle.mileage.toLocaleString()} km
                    </p>
                  </div>

                  {vehicle.color && (
                    <div>
                      <label className="text-xs text-gray-500 dark:text-gray-400">Szín</label>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{vehicle.color}</p>
                    </div>
                  )}

                  {vehicle.vin && (
                    <div>
                      <label className="text-xs text-gray-500 dark:text-gray-400">Alvázszám (VIN)</label>
                      <p className="text-sm font-mono text-gray-900 dark:text-white">{vehicle.vin}</p>
                    </div>
                  )}
                </div>

                {/* Műszaki adatok */}
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Műszaki adatok</h4>

                  {vehicle.engineCapacity && (
                    <div>
                      <label className="text-xs text-gray-500 dark:text-gray-400">Hengerűrtartalom</label>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {vehicle.engineCapacity} cm³
                      </p>
                    </div>
                  )}

                  {vehicle.power && (
                    <div>
                      <label className="text-xs text-gray-500 dark:text-gray-400">Teljesítmény</label>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {vehicle.power} kW ({Math.round(vehicle.power * 1.36)} LE)
                      </p>
                    </div>
                  )}

                  {vehicle.seats && (
                    <div>
                      <label className="text-xs text-gray-500 dark:text-gray-400">Ülések száma</label>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{vehicle.seats}</p>
                    </div>
                  )}

                  {vehicle.purchasePrice && (
                    <div>
                      <label className="text-xs text-gray-500 dark:text-gray-400">Vásárlási ár</label>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {vehicle.purchasePrice.toLocaleString()} HUF
                      </p>
                    </div>
                  )}

                  {vehicle.customerName && (
                    <div>
                      <label className="text-xs text-gray-500 dark:text-gray-400">Tulajdonos</label>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{vehicle.customerName}</p>
                    </div>
                  )}

                  {vehicle.assignedToName && (
                    <div>
                      <label className="text-xs text-gray-500 dark:text-gray-400">Jelenlegi vezető</label>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{vehicle.assignedToName}</p>
                    </div>
                  )}

                  {vehicle.driverLicenseNumber && (
                    <div>
                      <label className="text-xs text-gray-500 dark:text-gray-400">Jogosítvány</label>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{vehicle.driverLicenseNumber}</p>
                    </div>
                  )}
                </div>

                {/* Vezető megjegyzések */}
                {vehicle.driverNotes && (
                  <div className="md:col-span-2">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Vezető megjegyzések</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap bg-purple-50 dark:bg-purple-900/20 p-3 rounded-lg">
                      {vehicle.driverNotes}
                    </p>
                  </div>
                )}

                {/* Emlékeztetők */}
                <div className="md:col-span-2 space-y-4">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Emlékeztetők</h4>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {vehicle.nextServiceDate && (
                      <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                        <label className="text-xs text-blue-600 dark:text-blue-400">Következő szerviz</label>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {formatDate(vehicle.nextServiceDate)}
                        </p>
                        {vehicle.nextServiceMileage && (
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            vagy {vehicle.nextServiceMileage.toLocaleString()} km
                          </p>
                        )}
                      </div>
                    )}

                    {vehicle.insuranceExpiryDate && (
                      <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                        <label className="text-xs text-yellow-600 dark:text-yellow-400">Biztosítás lejárat</label>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {formatDate(vehicle.insuranceExpiryDate)}
                        </p>
                      </div>
                    )}

                    {vehicle.inspectionExpiryDate && (
                      <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                        <label className="text-xs text-red-600 dark:text-red-400">Műszaki vizsga</label>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {formatDate(vehicle.inspectionExpiryDate)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Megjegyzések */}
                {vehicle.notes && (
                  <div className="md:col-span-2">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Megjegyzések</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">
                      {vehicle.notes}
                    </p>
                  </div>
                )}

                {/* Km óra rögzítés - TILTOTT */}
                <div className="md:col-span-2 relative">
                  <div className="absolute inset-0 bg-gray-900/5 dark:bg-gray-900/20 backdrop-blur-[0.5px] z-10 rounded-lg flex items-center justify-center">
                    <div className="bg-white/95 dark:bg-gray-800/95 px-4 py-2 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">🔒 Ez a funkció jelenleg nem elérhető</p>
                    </div>
                  </div>
                  <div className="pointer-events-none opacity-70">
                    <MileageRecorder vehicle={vehicle} />
                  </div>
                </div>

                {/* Km óra előzmények */}
                <div className="md:col-span-2">
                  <MileageHistory vehicleId={vehicle.id} />
                </div>
              </div>
            )}

            {/* Services Tab */}
            {activeTab === 'services' && (
              <div className="space-y-4">
                {/* Szerviz rögzítés - TILTOTT */}
                <div className="relative">
                  <div className="absolute inset-0 bg-gray-900/5 dark:bg-gray-900/20 backdrop-blur-[0.5px] z-10 rounded-lg flex items-center justify-center">
                    <div className="bg-white/95 dark:bg-gray-800/95 px-4 py-2 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">🔒 Ez a funkció jelenleg nem elérhető</p>
                    </div>
                  </div>
                  <div className="pointer-events-none opacity-70">
                    <ServiceRecorder vehicle={vehicle} />
                  </div>
                </div>

                {servicesLoading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  </div>
                ) : services.length === 0 ? (
                  <div className="text-center py-8">
                    <WrenchScrewdriverIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Még nincs szerviz előzmény</p>
                  </div>
                ) : (
                  services.map((service) => (
                    <div
                      key={service.id}
                      className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h5 className="text-sm font-semibold text-gray-900 dark:text-white">
                            {getServiceTypeLabel(service.type)}
                          </h5>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {formatDate(service.date)} • {service.mileage.toLocaleString()} km
                          </p>
                        </div>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {service.totalCost.toLocaleString()} HUF
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{service.description}</p>
                      {service.serviceProvider && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Szerviz: {service.serviceProvider}
                        </p>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Expenses Tab */}
            {activeTab === 'expenses' && (
              <div className="space-y-4">
                {/* Költség rögzítés - TILTOTT */}
                <div className="relative">
                  <div className="absolute inset-0 bg-gray-900/5 dark:bg-gray-900/20 backdrop-blur-[0.5px] z-10 rounded-lg flex items-center justify-center">
                    <div className="bg-white/95 dark:bg-gray-800/95 px-4 py-2 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">🔒 Ez a funkció jelenleg nem elérhető</p>
                    </div>
                  </div>
                  <div className="pointer-events-none opacity-70">
                    <ExpenseRecorder vehicle={vehicle} />
                  </div>
                </div>

                {expensesLoading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  </div>
                ) : expenses.length === 0 ? (
                  <div className="text-center py-8">
                    <BanknotesIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Még nincs költség bejegyzés</p>
                  </div>
                ) : (
                  expenses.map((expense) => (
                    <div
                      key={expense.id}
                      className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h5 className="text-sm font-semibold text-gray-900 dark:text-white">
                            {getExpenseCategoryLabel(expense.category)}
                          </h5>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {formatDate(expense.date)}
                            {expense.mileage && ` • ${expense.mileage.toLocaleString()} km`}
                          </p>
                        </div>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {expense.amount.toLocaleString()} HUF
                        </span>
                      </div>
                      {expense.description && (
                        <p className="text-sm text-gray-600 dark:text-gray-400">{expense.description}</p>
                      )}
                      {expense.fuelAmount && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {expense.fuelAmount} liter
                          {expense.pricePerLiter && ` • ${expense.pricePerLiter} HUF/l`}
                        </p>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Documents Tab */}
            {activeTab === 'documents' && (
              <div className="space-y-6">
                {/* Dokumentum feltöltés - TILTOTT */}
                <div className="relative">
                  <div className="absolute inset-0 bg-gray-900/5 dark:bg-gray-900/20 backdrop-blur-[0.5px] z-10 rounded-lg flex items-center justify-center">
                    <div className="bg-white/95 dark:bg-gray-800/95 px-4 py-2 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">🔒 Ez a funkció jelenleg nem elérhető</p>
                    </div>
                  </div>
                  <div className="pointer-events-none opacity-70">
                    <VehicleDocumentUploader vehicle={vehicle} />
                  </div>
                </div>

                {/* Fotó feltöltés - ENGEDÉLYEZETT */}
                <VehiclePhotoUploader vehicle={vehicle} />

                {/* Photos */}
                {vehicle.photos && vehicle.photos.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Fotók</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {vehicle.photos.map((photo) => (
                        <div key={photo.id} className="aspect-square rounded-lg overflow-hidden">
                          <img
                            src={photo.url}
                            alt={photo.description || 'Vehicle photo'}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Documents */}
                {vehicle.documents && vehicle.documents.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Dokumentumok</h4>
                    <div className="space-y-2">
                      {vehicle.documents.map((doc) => (
                        <a
                          key={doc.id}
                          href={doc.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                        >
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">{doc.name}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {formatDate(doc.uploadedAt)}
                              {doc.expiryDate && ` • Lejár: ${formatDate(doc.expiryDate)}`}
                            </p>
                          </div>
                          <DocumentTextIcon className="w-5 h-5 text-gray-400" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {(!vehicle.photos || vehicle.photos.length === 0) &&
                  (!vehicle.documents || vehicle.documents.length === 0) && (
                    <div className="text-center py-8">
                      <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
                      <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                        Még nincsenek dokumentumok vagy fotók
                      </p>
                    </div>
                  )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleDetails;
